import * as React from "react"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { AXIOS_API_INSTANCE, customer_user_object, get_service_order_color, SHORTER_DATE_FORMAT } from "../../../Constants"
import { send_authorized_request } from "../../../Constants/cognito"
import { useSnackbar } from 'notistack';
import { Box, Checkbox, FormControlLabel, IconButton, Tab, Tabs } from "@mui/material"
import Grid from "@mui/material/Grid2"
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress } from '@mui/material';
import dayjs from "dayjs"
import ServiceEvent, { get_service_event_text } from "../ServiceEvent"
import ServiceNote from "../ServiceNote"
import TabPanel from "../../../Utils/tabpanel"
import ReactApexChart from "react-apexcharts"
import { useTrackedUser } from "../../UserState"
import { findUser, useUsers } from "../../ReactQuery/QueryUsers"
import AIButton from "../../../Utils/AIButton"

export default function ServiceOrderEventsModal({
    customerItem,
    open,
    serviceOrder,
    closeModal
}) {
    const { enqueueSnackbar } = useSnackbar();
    const { data: users, } = useUsers();
    const [my_user,] = useTrackedUser();
    const [tab, setTab] = React.useState("events")
    const [serviceOrderEvents, setServiceOrderEvents] = React.useState(undefined)
    const [serviceOrderEventsLoadingStep, setServiceOrderEventsLoadingStep] = React.useState(0)
    const [interleaveEvents, setInterleaveEvents] = React.useState(true)

    const getEvents = () => {
        setServiceOrderEventsLoadingStep(1)
        send_authorized_request(() => AXIOS_API_INSTANCE.get(`serviceorders/${serviceOrder.id}/events`).then((serviceOrderEvents) => {
            const parts_list = serviceOrder.service_order_services.map((x) => x.service_order_parts).flat()
            setServiceOrderEvents(serviceOrderEvents.data.data.events.concat(
                serviceOrderEvents.data.data.part_status_events.map((x) => {
                    return { ...x, event_type: 'CHANGEPARTSTATUS', service_order_part: parts_list.find((p) => p.id === x.service_order_part_id) }
                })))
            setServiceOrderEventsLoadingStep(0)
        }).catch((error) => {
            console.log(error);
            enqueueSnackbar(`Failed to load service order events: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
            setServiceOrderEventsLoadingStep(0)
        }))
    }

    React.useEffect(() => {
        if (serviceOrder) {
            setServiceOrderEvents(undefined)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceOrder])

    React.useEffect(() => {
        if (serviceOrderEvents === undefined && open) {
            getEvents()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, serviceOrderEvents]);

    const interleaved_events = (serviceOrderEvents ?? []).map((event) => { return { object_type: 'event', ...event } }).concat(
        (serviceOrder?.service_order_notes ?? []).map((event) => { return { object_type: 'note', ...event } })
    ).concat((serviceOrder?.service_order_work_history || []).map((x) => {
        const return_list = []
        if (x.diagnostic_note) {
            return_list.push({
                id: `diagnostic-${x.id}`,
                date_created: x.end_time,
                user_id: x.user_id,
                text: x.diagnostic_note,
                type: "diagnostic",
                object_type: 'note'
            })
        }
        if (x.overhead_note) {
            return_list.push({
                id: `overhead-${x.id}`,
                date_created: x.end_time,
                user_id: x.user_id,
                text: x.overhead_note,
                type: "overhead",
                object_type: 'note'
            })
        }
        x.service_order_work_history_entries.forEach((entry) => {
            const service_order_service_name = serviceOrder.service_order_services.find((service) => service.id === entry.service_order_service_id)?.name ?? 'Unknown Service'
            if (entry.note) {
                return_list.push({
                    id: `service-${x.id}-${entry.id}`,
                    date_created: x.end_time,
                    user_id: x.user_id,
                    text: entry.note,
                    type: "work_history",
                    object_type: 'note',
                    service_name: service_order_service_name,
                })
            }
        })
        return return_list
    }).flat()).sort((a, b) => {
        const a_time = dayjs.utc(a.date_created).unix();
        const b_time = dayjs.utc(b.date_created).unix();
        if (a_time === b_time) {
            // show events first
            if (a.object_type === 'event' && b.object_type === 'note') {
                return -1
            } else if (a.object_type === 'note' && b.object_type === 'event') {
                return 1
            } else {
                return 0
            }
        }
        return b_time - a_time
    })

    const timeline_series = [];
    if (serviceOrderEvents && serviceOrderEvents.length > 0) {
        const creation_event = (serviceOrderEvents ?? []).find((event) => event.event_type === "CREATION")
        timeline_series.push({
            x: creation_event?.request_body?.status_name ?? 'Unknown',
            y: [
                dayjs.utc(creation_event?.date_created).toDate().getTime()
            ],
            fillColor: get_service_order_color(my_user.client.service_order_statuses, creation_event?.request_body?.status_name).toUpperCase()
        });
        (serviceOrderEvents ?? []).sort((a, b) => dayjs.utc(a.date_created).unix() - dayjs.utc(b.date_created).unix()).filter((event) => event.event_type === "CHANGESERVICEORDERSTATUS").forEach((event) => {
            timeline_series.at(-1).y.push(dayjs.utc(event.date_created).toDate().getTime() - 1)
            timeline_series.push({
                x: event.request_body?.status ?? 'Unknown',
                y: [
                    dayjs.utc(event.date_created).toDate().getTime()
                ],
                fillColor: get_service_order_color(my_user.client.service_order_statuses, event.request_body?.status).toUpperCase()
            })
        })
        if (['COMPLETED', 'DECLINED'].includes(my_user.client.service_order_statuses[timeline_series.at(-1).x]?.type)) {
            const maximum_one_day = Math.min(dayjs.utc().toDate().getTime(), timeline_series.at(-1).y[0] + (1000 * 60 * 60 * 24))
            timeline_series.at(-1).y.push(maximum_one_day)
        } else {
            timeline_series.at(-1).y.push(dayjs.utc().toDate().getTime())
        }
    }

    return (
        <div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>Service Order Events</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {serviceOrderEventsLoadingStep > 0 &&
                        <CircularProgress style={{ display: 'block', margin: '20% auto' }} color="inherit" />
                    }
                    {!serviceOrderEventsLoadingStep > 0 && serviceOrderEvents?.length === 0 &&
                        <h3>No Events</h3>
                    }
                    {!serviceOrderEventsLoadingStep > 0 && serviceOrderEvents?.length > 0 &&
                        <Box>
                            <Tabs
                                value={tab}
                                onChange={(e, value) => setTab(value)}
                                variant="scrollable"
                            >
                                <Tab label="Events" value="events" sx={{ fontWeight: "bold" }} />
                                <Tab label="Timeline" value="timeline" sx={{ fontWeight: "bold" }} />
                            </Tabs >
                            <TabPanel value={tab} value_key={"events"} sx={{ p: 0, pt: 1 }} preserve>
                                <Grid container direction={'column'} spacing={1}>
                                    <Grid container justifyContent={'space-between'}>
                                        <Grid size={"auto"}>
                                            <FormControlLabel
                                                control={<Checkbox checked={interleaveEvents}
                                                    onChange={() => setInterleaveEvents(!interleaveEvents)} />}
                                                label="Include Notes"
                                                labelPlacement='start'
                                                sx={{ pb: 3 }}
                                            />
                                        </Grid>
                                        <Grid size={"auto"}>
                                            <AIButton make_prompt={() => {
                                                const prefix = `Please summarize the following service order events and notes chronologically by day in a professional manner. Do not show seconds. Use 12 hour time. Use timezone ${Intl.DateTimeFormat().resolvedOptions().timeZone}. Please include the most important details and avoid unnecessary information. Here are the events in json format: `
                                                const events = interleaved_events.map((event) => {
                                                    const user = event.user_id ? findUser(users, event.user_id) : customer_user_object
                                                    return {
                                                        datetime: dayjs.utc(event.date_created).local().format(),
                                                        actor: user?.name,
                                                        event: `${event.text ?? get_service_event_text(event)}`
                                                    }
                                                })
                                                return { prompt: `${prefix} ${JSON.stringify(events)}` }
                                            }
                                            }>
                                                Recap
                                            </AIButton>
                                        </Grid>
                                    </Grid>
                                    {interleaveEvents ?
                                        interleaved_events.map((thing) => {
                                            if (thing.object_type === 'event') {
                                                return (<ServiceEvent
                                                    key={thing.id}
                                                    serviceEvent={thing}
                                                />)
                                            } else {
                                                return (<ServiceNote
                                                    key={thing.id}
                                                    serviceNote={thing}
                                                />)
                                            }
                                        }
                                        )
                                        :
                                        (serviceOrderEvents ?? []).sort((a, b) => dayjs.utc(b.date_created).unix() - dayjs.utc(a.date_created).unix()).map((event) =>
                                            <ServiceEvent
                                                key={event.id}
                                                serviceEvent={event}
                                            />
                                        )
                                    }
                                </Grid>
                            </TabPanel>
                            <TabPanel value={tab} value_key={"timeline"} sx={{ p: 0, pt: 1 }} preserve>
                                <ReactApexChart
                                    options={{
                                        chart: {
                                            height: 350,
                                            type: 'rangeBar'
                                        },
                                        plotOptions: {
                                            bar: {
                                                horizontal: true,
                                                distributed: true,
                                                dataLabels: {
                                                    hideOverflowingLabels: false
                                                },
                                                colors: {
                                                    ranges: timeline_series.map((event) => {
                                                        return {
                                                            from: event.y[0],
                                                            to: event.y[1],
                                                            color: event.fillColor
                                                        }
                                                    })
                                                }
                                            }
                                        },
                                        // dataLabels: {
                                        //     enabled: true,
                                        //     formatter: function (val, opts) {
                                        //         var label = opts.w.globals.labels[opts.dataPointIndex]
                                        //         var a = moment(val[0])
                                        //         var b = moment(val[1])
                                        //         var diff = b.diff(a, 'days')
                                        //         return label + ': ' + diff + (diff > 1 ? ' days' : ' day')
                                        //     },
                                        //     style: {
                                        //         colors: ['#f3f4f5', '#fff']
                                        //     }
                                        // },
                                        xaxis: {
                                            type: 'datetime'
                                        },
                                        yaxis: {
                                            show: true
                                        },
                                        tooltip: {
                                            x: {
                                                formatter: function (value, time, c, d, e) {
                                                    if (Number.isInteger(value)) {
                                                        return dayjs.utc(value).local().format(SHORTER_DATE_FORMAT)
                                                    } else {
                                                        return 'Time'
                                                    }
                                                },
                                                // title: {
                                                //     formatter: (seriesName) => { return (users && users.data.data.find((x) => x.id === seriesName)?.name) ?? seriesName },
                                                // },
                                            },
                                        },
                                        grid: {
                                            row: {
                                                colors: ['#f3f4f5', '#fff'],
                                                opacity: 1
                                            }
                                        }
                                    }}
                                    series={[{ data: timeline_series }]}
                                    type="rangeBar"
                                    height={350}
                                />
                            </TabPanel>
                        </Box>
                    }
                </DialogContent>
            </Dialog>
        </div>
    )
}
